if (!customElements.get('custom-product-configurator')) {
  customElements.define(
    'custom-product-configurator',
    class CustomProductConfigurator extends HTMLElement {
      constructor() {
        super();
        this.variantInputs = Array.from(this.querySelectorAll('.product-configurator__radio'));
        this.addonInput = this.querySelector('.product-configurator__addon-checkbox');
        this.addonContainer = this.querySelector('[data-configurator-addon]');
        this.addonPriceElement = this.querySelector('[data-configurator-addon-price]');
        this.addonSoldOutElement = this.querySelector('[data-configurator-addon-sold-out]');
        this.quantityInput = document.getElementById(`Quantity-${this.dataset.sectionId}`);
        this.errorElement = this.querySelector('[data-configurator-error]');
        this.totalElement = this.querySelector('[data-configurator-total]');
        this.priceRequest = null;

        this.variantInputs.forEach((input) => input.addEventListener('change', () => this.onVariantChange(input)));
        this.addonInput?.addEventListener('change', () => this.onAddonChange());
        this.quantityInput?.addEventListener('change', () => this.updateTotal());
        this.querySelectorAll('[data-configurator-preview]').forEach((button) => {
          button.addEventListener('click', (event) => this.openPreview(event));
        });
        this.querySelectorAll('[data-configurator-close]').forEach((button) => {
          button.addEventListener('click', () => button.closest('dialog')?.close());
        });
        this.querySelectorAll('dialog').forEach((dialog) => {
          dialog.addEventListener('click', (event) => {
            if (event.target === dialog) dialog.close();
          });
        });
        this.querySelectorAll('[data-configurator-gallery]').forEach((gallery) => this.initializeGallery(gallery));

        this.installCartHook();
      }

      connectedCallback() {
        const selected = this.selectedVariant;
        if (selected) {
          this.applyQuantityRules(selected);
          this.syncAddonForVariant(selected);
          this.syncSelectedVariant();
        }
        this.updateTotal();
      }

      get selectedVariant() {
        return this.variantInputs.find((input) => input.checked && !input.disabled);
      }

      get quantity() {
        const parsed = Number.parseInt(this.quantityInput?.value || '1', 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
      }

      get addonMode() {
        return this.dataset.addonMode || 'none';
      }

      get activeVariantId() {
        const selected = this.selectedVariant;
        if (!selected) return '';
        if (this.addonMode === 'paired' && this.addonInput?.checked && selected.dataset.addonVariantId) {
          return selected.dataset.addonVariantId;
        }
        return selected.value;
      }

      get addonUnitPrice() {
        if (this.addonMode === 'paired') {
          return Number.parseInt(this.selectedVariant?.dataset.addonPrice || '0', 10);
        }
        return Number.parseInt(this.dataset.addonPrice || '0', 10);
      }

      formatMoney(cents) {
        const currency = this.dataset.currencyCode || 'USD';
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency,
          }).format(cents / 100);
        } catch (_error) {
          return `${currency} ${(cents / 100).toFixed(2)}`;
        }
      }

      onVariantChange(input) {
        if (input.disabled) return;
        this.applyQuantityRules(input);
        this.syncAddonForVariant(input);
        this.updateTotal();
        this.syncSelectedVariant();
      }

      onAddonChange() {
        if (this.addonInput?.disabled) return;
        this.updateTotal();
        if (this.addonMode === 'paired') this.syncSelectedVariant();
      }

      syncSelectedVariant() {
        const variantId = this.activeVariantId;
        if (!variantId) return;
        this.dataset.selectedVariantId = variantId;
        this.updateProductPrice(variantId);
        this.syncProductForm(variantId);

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      }

      // Keep the theme's own product form (and so TimesAct) pointed at the chosen variant.
      syncProductForm(variantId) {
        document
          .querySelectorAll(`form[id$="-${this.dataset.sectionId}"] input[name="id"]`)
          .forEach((input) => {
            input.value = variantId;
            input.disabled = false;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        document.dispatchEvent(new CustomEvent('configurator:variant-change', { detail: { variantId } }));
      }

      // Add the separate Extra Paint Set product when the theme / TimesAct button submits.
      installCartHook() {
        if (window.__configuratorCartHooked) return;
        window.__configuratorCartHooked = true;
        const originalFetch = window.fetch.bind(window);

        window.fetch = async (input, init) => {
          const url = typeof input === 'string' ? input : input?.url || '';
          if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
            const configurator = document.querySelector('custom-product-configurator');
            if (configurator?.shouldAddAddon(init?.body)) {
              try {
                await configurator.addAddon(originalFetch);
              } catch (error) {
                configurator.showError(error.message);
                throw error;
              }
            }
          }
          return originalFetch(input, init);
        };
      }

      shouldAddAddon(body) {
        if (this.addonMode !== 'separate') return false;
        if (!this.addonInput?.checked || !this.dataset.addonVariantId) return false;

        // Only when the request is for this product (not quick-add on other products).
        let requestedId = '';
        if (body instanceof FormData) {
          requestedId = String(body.get('id') || '');
        } else if (typeof body === 'string') {
          try {
            const parsed = JSON.parse(body);
            requestedId = String(parsed.id ?? parsed.items?.[0]?.id ?? '');
          } catch (_error) {
            return false;
          }
        }
        return requestedId === String(this.activeVariantId);
      }

      async addAddon(fetchFn) {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetchFn(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: Number.parseInt(this.dataset.addonVariantId, 10),
                quantity: this.dataset.addonQuantity === 'one' ? 1 : this.quantity,
              },
            ],
          }),
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || 'Unable to add the Extra Paint Set.');
        }
        this.showError();
      }

      syncAddonForVariant(input) {
        if (this.addonMode !== 'paired' || !this.addonInput) return;

        const addonVariantId = input.dataset.addonVariantId || '';
        const addonPrice = Number.parseInt(input.dataset.addonPrice || '0', 10);
        const addonAvailable = Boolean(addonVariantId) && input.dataset.addonAvailable === 'true';

        this.dataset.addonVariantId = addonVariantId;
        this.dataset.addonPrice = String(addonPrice);
        this.addonInput.disabled = !addonAvailable;
        if (!addonAvailable) this.addonInput.checked = false;
        this.addonContainer?.classList.toggle('is-unavailable', !addonAvailable);
        if (this.addonSoldOutElement) {
          this.addonSoldOutElement.hidden = addonAvailable;
          this.addonSoldOutElement.setAttribute('aria-hidden', String(addonAvailable));
          this.addonSoldOutElement.style.setProperty('display', addonAvailable ? 'none' : '', 'important');
        }
        if (this.addonPriceElement) this.addonPriceElement.textContent = `(+${this.formatMoney(addonPrice)})`;
      }

      applyQuantityRules(input) {
        if (!this.quantityInput) return;
        const min = Number.parseInt(input.dataset.quantityMin || '1', 10);
        const max = Number.parseInt(input.dataset.quantityMax || '', 10);
        const step = Number.parseInt(input.dataset.quantityStep || '1', 10);
        this.quantityInput.min = String(min);
        this.quantityInput.step = String(step);
        if (Number.isFinite(max)) this.quantityInput.max = String(max);
        else this.quantityInput.removeAttribute('max');
        if (Number.parseInt(this.quantityInput.value, 10) < min) this.quantityInput.value = String(min);
        if (Number.isFinite(max) && Number.parseInt(this.quantityInput.value, 10) > max) {
          this.quantityInput.value = String(max);
        }
      }

      updateTotal() {
        if (!this.totalElement) return;
        const selected = this.selectedVariant;
        if (!selected) {
          this.totalElement.textContent = '';
          return;
        }

        const quantity = this.quantity;
        let total = Number.parseInt(selected.dataset.variantPrice || '0', 10) * quantity;
        if (this.addonInput?.checked) {
          const addonQuantity =
            this.addonMode === 'paired' || this.dataset.addonQuantity !== 'one' ? quantity : 1;
          total += this.addonUnitPrice * addonQuantity;
        }
        this.totalElement.textContent = this.formatMoney(total);
      }

      async updateProductPrice(variantId) {
        this.priceRequest?.abort();
        this.priceRequest = new AbortController();

        try {
          const url = new URL(this.dataset.productUrl, window.location.origin);
          url.searchParams.set('variant', variantId);
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url.toString(), { signal: this.priceRequest.signal });
          if (!response.ok) return;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const currentPrice = document.getElementById(`price-${this.dataset.sectionId}`);
          const updatedPrice = html.getElementById(`price-${this.dataset.sectionId}`);
          if (currentPrice && updatedPrice) currentPrice.innerHTML = updatedPrice.innerHTML;
        } catch (error) {
          if (error.name !== 'AbortError') console.error(error);
        }
      }

      openPreview(event) {
        event.preventDefault();
        event.stopPropagation();
        const dialog = document.getElementById(event.currentTarget.dataset.configuratorPreview);
        if (!dialog) return;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      }

      initializeGallery(gallery) {
        const track = gallery.querySelector('[data-configurator-gallery-track]');
        const previousButton = gallery.querySelector('[data-configurator-gallery-previous]');
        const nextButton = gallery.querySelector('[data-configurator-gallery-next]');
        const currentSlide = gallery.querySelector('[data-configurator-gallery-current]');
        if (!track || !previousButton || !nextButton || !currentSlide) return;

        const updateControls = () => {
          const slideWidth = track.clientWidth;
          const index = slideWidth ? Math.round(track.scrollLeft / slideWidth) : 0;
          const lastIndex = track.children.length - 1;
          currentSlide.textContent = String(index + 1);
          previousButton.disabled = index <= 0;
          nextButton.disabled = index >= lastIndex;
        };

        previousButton.addEventListener('click', () => {
          track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' });
        });
        nextButton.addEventListener('click', () => {
          track.scrollBy({ left: track.clientWidth, behavior: 'smooth' });
        });
        track.addEventListener('scroll', updateControls, { passive: true });
        updateControls();
      }

      showError(message = '') {
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.hidden = !message;
      }
    }
  );
}